import {
  buildRingCentralStylesUri,
  isRingCentralEnabled,
  RING_CENTRAL_UI_HIDE_CSS,
} from '../components/shared/Topbar/components/ringCentral/ringCentralConfig';

import { formatPhoneNumber } from './Helpers';

export const RING_CENTRAL_FRAME_ID = 'pf-ringcentral-softphone-frame';
const RING_CENTRAL_OPEN_EVENT = 'pf:ringcentral-open';
const RING_CENTRAL_DIAL_EVENT = 'pf:ringcentral-dial';
const RING_CENTRAL_DIAL_FAILED_EVENT = 'pf:ringcentral-dial-failed';
const RING_CENTRAL_STATUS_EVENT = 'pf:ringcentral-status';
const RING_CENTRAL_INCOMING_EVENT = 'pf:ringcentral-incoming';
const RING_CENTRAL_INCOMING_CLEAR_EVENT = 'pf:ringcentral-incoming-clear';
const RING_CENTRAL_CALL_END_EVENT = 'pf:ringcentral-call-end';
const RING_CENTRAL_CALL_HISTORY_SYNCED_EVENT =
  'pf:ringcentral-call-history-synced';
const RING_CENTRAL_ACTIVE_CALL_EVENT = 'pf:ringcentral-active-call';
const RING_CENTRAL_INBOUND_PROJECT_LINKED_EVENT =
  'pf:ringcentral-inbound-project-linked';

const READINESS_WAIT_MS = 45000;
const CALL_START_WAIT_MS = 20000;

type TimerRef = { current: ReturnType<typeof setTimeout> | null };
type IntervalRef = { current: ReturnType<typeof setInterval> | null };

export type IncomingCallDetail = {
  callId?: string;
  telephonySessionId?: string;
  sessionId?: string;
  name: string;
  phoneNumber: string;
  direction?: string;
  telephonyStatus?: string;
};

export type CallEndDetail = {
  callId?: string;
  telephonySessionId?: string;
  sessionId?: string;
  rcCallLogId?: string;
  phoneNumber: string;
  from: string;
  to: string;
  direction?: string;
  startTime?: number;
  duration?: number;
  durationMs?: number;
  status?: string;
  callerName?: string;
  receiverName?: string;
  recordingUri?: string;
  transcript?: string | null;
  awaitingTelephonyId?: boolean;
};

export type CallHistorySyncedDetail = CallEndDetail & {
  syncRecords?: import('./ringCentralCallHistory').RingCentralCallLogRecord[];
  syncToken?: string;
  priorSyncToken?: string;
  awaitingTelephonyId?: boolean;
};

type SyncRecord = NonNullable<CallHistorySyncedDetail['syncRecords']>[number];

export type DialWithRingCentralOptions = {
  customerId?: string | number;
  projectId?: string | number;
};

export const RC_CUSTOMER_ID_KEY = 'rc_customer_id';
export const PF_RC_OUTBOUND_PROJECT_ID_KEY = 'pf_rc_outbound_project_id';
export const PF_RC_OUTBOUND_CUSTOMER_ID_KEY = 'pf_rc_outbound_customer_id';
export const PF_RC_INBOUND_PROJECT_ID_KEY = 'pf_rc_inbound_project_id';
const PF_RC_ACCESS_TOKEN_KEY = 'pf-ringcentral-access-token';

const clearPfRcAccessToken = () => {
  try {
    localStorage.removeItem(PF_RC_ACCESS_TOKEN_KEY);
  } catch {
    return;
  }
};

const parseNumericId = (value?: string | number | null): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCustomerId = parseNumericId;
const parseProjectId = parseNumericId;

const migrateStorageValue = (key: string): string | null => {
  try {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal != null) return fromLocal;
    const legacy = sessionStorage.getItem(key);
    if (legacy != null) {
      localStorage.setItem(key, legacy);
      sessionStorage.removeItem(key);
      return legacy;
    }
  } catch {
    return null;
  }
  return null;
};

const setLocalStorageValue = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
    sessionStorage.removeItem(key);
  } catch {
    return;
  }
};

const removeLocalStorageValue = (key: string) => {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    return;
  }
};

let clickToCallActive = false;

export const setRcCustomerId = (customerId?: string | number | null) => {
  const parsed = parseCustomerId(customerId);
  if (parsed == null) {
    removeLocalStorageValue(RC_CUSTOMER_ID_KEY);
    return;
  }
  setLocalStorageValue(RC_CUSTOMER_ID_KEY, String(parsed));
};

export type OutboundCallContext = {
  customerId?: string | number | null;
  projectId?: string | number | null;
};

const resolveProjectIdForClickToCall = (
  projectId?: string | number | null
): number | null => parseProjectId(projectId);

const resolveCustomerIdForClickToCall = (
  customerId?: string | number | null
): number | null => parseCustomerId(customerId);

/** Persist outbound call context when the user confirms a click-to-call. */
export const storeOutboundCallContext = ({
  customerId,
  projectId,
}: OutboundCallContext = {}) => {
  clearInboundCallContext();
  const resolvedCustomerId = resolveCustomerIdForClickToCall(customerId);
  const resolvedProjectId = resolveProjectIdForClickToCall(projectId);

  if (resolvedCustomerId != null) {
    setLocalStorageValue(
      PF_RC_OUTBOUND_CUSTOMER_ID_KEY,
      String(resolvedCustomerId)
    );
  }
  if (resolvedProjectId != null) {
    setLocalStorageValue(
      PF_RC_OUTBOUND_PROJECT_ID_KEY,
      String(resolvedProjectId)
    );
  }
};

export const hasOutboundCallContext = (): boolean =>
  migrateStorageValue(PF_RC_OUTBOUND_PROJECT_ID_KEY) != null ||
  migrateStorageValue(PF_RC_OUTBOUND_CUSTOMER_ID_KEY) != null;

export const clearOutboundCallContext = () => {
  clickToCallActive = false;
  removeLocalStorageValue(PF_RC_OUTBOUND_PROJECT_ID_KEY);
  removeLocalStorageValue(PF_RC_OUTBOUND_CUSTOMER_ID_KEY);
  removeLocalStorageValue(RC_CUSTOMER_ID_KEY);
};

export const getCallLogCustomerId = (): number | null =>
  parseCustomerId(migrateStorageValue(PF_RC_OUTBOUND_CUSTOMER_ID_KEY));

export const getCallLogProjectId = (): number | null =>
  parseProjectId(migrateStorageValue(PF_RC_OUTBOUND_PROJECT_ID_KEY));

export const isInboundCallDirection = (direction?: string | null): boolean =>
  String(direction || '')
    .toLowerCase()
    .includes('inbound');

const readInboundProjectIdFromStorage = (): string | null =>
  migrateStorageValue(PF_RC_INBOUND_PROJECT_ID_KEY);

export const storeInboundCallProjectId = (
  projectId?: string | number | null
) => {
  const resolved = parseProjectId(projectId);
  if (resolved == null) return;
  if (hasInboundCallContext()) return;

  setLocalStorageValue(PF_RC_INBOUND_PROJECT_ID_KEY, String(resolved));

  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_INBOUND_PROJECT_LINKED_EVENT, {
      detail: { projectId: resolved },
    })
  );
};

export const hasInboundCallContext = (): boolean =>
  readInboundProjectIdFromStorage() != null;

export const clearInboundCallContext = () => {
  removeLocalStorageValue(PF_RC_INBOUND_PROJECT_ID_KEY);
};

export const getInboundCallLogProjectId = (): number | null =>
  parseProjectId(readInboundProjectIdFromStorage());

export const clearCallLogCustomerContext = () => {
  clearOutboundCallContext();
  removeLocalStorageValue(RC_CUSTOMER_ID_KEY);
};

type RcCallParty = {
  phoneNumber?: string;
  extensionNumber?: string;
  name?: string;
  extensionName?: string;
};

type RcCall = {
  id?: string;
  callId?: string;
  sessionId?: string;
  telephonySessionId?: string;
  callerId?: string;
  fromName?: string;
  toName?: string;
  direction?: string;
  telephonyStatus?: string;
  callStatus?: string;
  startTime?: number;
  duration?: number;
  durationMs?: number;
  result?: string;
  from?: RcCallParty;
  to?: RcCallParty;
  recording?: {
    contentUri?: string;
    link?: string;
    uri?: string;
  };
  transcript?: string | null;
};

type WidgetMessage = {
  type?: string;
  ready?: boolean;
  loggedIn?: boolean;
  autoLog?: boolean;
  connectionStatus?: string;
  callSetting?: { fromNumbers?: string[] };
  fromNumbers?: string[];
  call?: RcCall;
  path?: string;
  requestId?: string;
  body?: {
    call?: RcCall;
    sessionIds?: string[];
  } & Record<string, unknown>;
  records?: CallHistorySyncedDetail['syncRecords'];
  syncInfo?: { syncType?: string; syncToken?: string };
  callLogSync?: {
    records?: CallHistorySyncedDetail['syncRecords'];
    syncInfo?: { syncType?: string; syncToken?: string };
  };
  telephonySession?: {
    id?: string;
    sessionId?: string;
  };
};

const RC_CRM_SERVICE_NAME = 'ProjectsForce';
const RC_CRM_CALL_LOGGER_PATH = '/pf-crm-call-logger';
const RC_CRM_CALL_LOGGER_MATCH_PATH = '/pf-crm-call-logger/match';

let pendingDialNumber: string | null = null;
let pendingDialRaw: string | null = null;
let lastDialedPhone: string | null = null;
let lastDialedAt = 0;
const LAST_DIALED_PHONE_TTL_MS = 5 * 60 * 1000;
let dialListenerInitialized = false;
let rcCrmServiceRegistered = false;
let isUserLoggedIn = false;
let isDialerReady = false;
let isWebphoneConnected = false;
let outboundCallPlaced = false;
let availableFromNumbers: string[] = [];
let activeIncomingCall: IncomingCallDetail | null = null;

type TelephonySessionCache = {
  telephonySessionId?: string;
  sessionId?: string;
  partyId?: string;
};

let lastTelephonySession: TelephonySessionCache | null = null;

export type ActiveCallDetail = {
  callId?: string;
  telephonySessionId?: string;
  sessionId?: string;
  name: string;
  phoneNumber: string;
  direction?: string;
  connectedAtMs?: number;
  durationSeconds?: number;
};

export const isActiveInboundCall = (call?: ActiveCallDetail | null): boolean =>
  isInboundCallDirection(call?.direction);

export const resolveActiveCallElapsedSeconds = (
  call?: ActiveCallDetail | null,
  now = Date.now()
): number => {
  if (!call?.connectedAtMs) return 0;
  return Math.max(0, Math.floor((now - call.connectedAtMs) / 1000));
};

const isPartyId = (value?: string | null) => Boolean(value?.startsWith('p-'));

const isTelephonySessionId = (value?: string | null) =>
  Boolean(value?.startsWith('s-'));

const resolveTelephonySessionId = (call?: RcCall | null) => {
  if (!call) return undefined;
  if (call.telephonySessionId) return call.telephonySessionId;
  if (isTelephonySessionId(call.sessionId)) return call.sessionId;
  if (isTelephonySessionId(call.id)) return call.id;
  return undefined;
};

const resolveLegacySessionId = (call?: RcCall | null) => {
  if (!call) return undefined;
  if (call.sessionId && !isTelephonySessionId(call.sessionId)) {
    return call.sessionId;
  }
  if (
    call.callId &&
    !isPartyId(call.callId) &&
    !isTelephonySessionId(call.callId)
  ) {
    return call.callId;
  }
  if (call.id && !isPartyId(call.id) && !isTelephonySessionId(call.id)) {
    return call.id;
  }
  return undefined;
};

let activeCall: ActiveCallDetail | null = null;

const emitStatus = () => {
  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_STATUS_EVENT, {
      detail: {
        loggedIn: isUserLoggedIn,
        dialerReady: isDialerReady,
        webphoneConnected: isWebphoneConnected,
        fromNumbers: [...availableFromNumbers],
        widgetActive: Boolean(getSoftphoneFrame()?.contentWindow),
      },
    })
  );
};

const parseIncomingCall = (call?: RcCall | null): IncomingCallDetail | null => {
  if (!call) return null;
  const from = call.from || {};
  const phoneNumber =
    from.phoneNumber || from.extensionNumber || call.callerId || '';
  const telephonySessionId = resolveTelephonySessionId(call);
  const sessionId = resolveLegacySessionId(call);

  return {
    callId: telephonySessionId || sessionId || call.id,
    telephonySessionId,
    sessionId,
    name: call.fromName || from.name || from.extensionName || 'Unknown',
    phoneNumber: String(phoneNumber || ''),
    direction: call.direction,
    telephonyStatus: call.telephonyStatus,
  };
};

const isInboundRinging = (call?: RcCall | null) => {
  const direction = String(call?.direction || '').toLowerCase();
  const status = String(call?.telephonyStatus || '').toLowerCase();
  return (
    direction.includes('inbound') &&
    (status.includes('ring') || status === 'callingsessionringing')
  );
};

const normalizeRcTimestampMs = (value?: number): number | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined;
  return value < 1e12 ? value * 1000 : value;
};

const resolveDurationSecondsFromRcCall = (call: RcCall): number | undefined => {
  if (call.durationMs != null && call.durationMs >= 0) {
    return Math.floor(call.durationMs / 1000);
  }
  if (call.duration != null && call.duration >= 0) {
    return call.duration;
  }
  return undefined;
};

const resolveConnectedAtMsFromRcCall = (
  call: RcCall,
  durationSeconds?: number
): number | undefined => {
  const startMs = normalizeRcTimestampMs(call.startTime);
  if (startMs) return startMs;
  if (durationSeconds != null && durationSeconds >= 0) {
    return Date.now() - durationSeconds * 1000;
  }
  return undefined;
};

const enrichActiveCallTiming = (
  call: ActiveCallDetail,
  prev?: ActiveCallDetail | null,
  options?: { connectedNow?: boolean }
): ActiveCallDetail => {
  const sameCall = prev?.callId != null && prev.callId === call.callId;
  let connectedAtMs =
    call.connectedAtMs ?? (sameCall ? prev?.connectedAtMs : undefined);
  const durationSeconds =
    call.durationSeconds ?? (sameCall ? prev?.durationSeconds : undefined);

  if (options?.connectedNow && connectedAtMs == null) {
    connectedAtMs = Date.now();
  }

  if (durationSeconds != null && connectedAtMs == null) {
    connectedAtMs = Date.now() - durationSeconds * 1000;
  }

  return {
    ...call,
    connectedAtMs,
    durationSeconds,
  };
};

const parseActiveCall = (call?: RcCall | null): ActiveCallDetail | null => {
  if (!call) return null;
  const from = call.from || {};
  const to = call.to || {};
  const direction = String(call.direction || '').toLowerCase();
  const party = direction.includes('outbound') ? to : from;
  const phoneNumber =
    party.phoneNumber ||
    party.extensionNumber ||
    from.phoneNumber ||
    from.extensionNumber ||
    call.callerId ||
    '';
  if (!phoneNumber && !call.fromName && !party.name) return null;

  const telephonySessionId = resolveTelephonySessionId(call);
  const sessionId = resolveLegacySessionId(call);
  const durationSeconds = resolveDurationSecondsFromRcCall(call);
  const connectedAtMs = resolveConnectedAtMsFromRcCall(call, durationSeconds);

  return {
    callId: telephonySessionId || sessionId || call.id,
    telephonySessionId,
    sessionId,
    name:
      party.name ||
      party.extensionName ||
      call.fromName ||
      from.name ||
      from.extensionName ||
      'Unknown',
    phoneNumber: String(phoneNumber || ''),
    direction: call.direction,
    connectedAtMs,
    durationSeconds,
  };
};

const isConnectedCall = (call?: RcCall | null) => {
  if (!call) return false;
  if (isInboundRinging(call)) return false;
  const status = String(call.telephonyStatus || '').toLowerCase();
  if (!status || status.includes('idle') || status.includes('disconnected')) {
    return false;
  }
  return (
    status.includes('connected') ||
    status.includes('hold') ||
    status.includes('oncall') ||
    status.includes('answered') ||
    status.includes('progress') ||
    status.includes('calling')
  );
};

const emitActiveCall = (
  call: ActiveCallDetail | null,
  options?: { connectedNow?: boolean }
) => {
  if (!call) {
    if (!activeCall) return;
    activeCall = null;
    window.dispatchEvent(
      new CustomEvent(RING_CENTRAL_ACTIVE_CALL_EVENT, { detail: null })
    );
    return;
  }

  const enriched = enrichActiveCallTiming(call, activeCall, options);
  const prev = activeCall;
  const unchanged =
    prev?.callId === enriched.callId &&
    prev?.name === enriched.name &&
    prev?.phoneNumber === enriched.phoneNumber &&
    prev?.direction === enriched.direction &&
    prev?.connectedAtMs === enriched.connectedAtMs &&
    prev?.durationSeconds === enriched.durationSeconds;

  if (unchanged) return;

  activeCall = enriched;
  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_ACTIVE_CALL_EVENT, { detail: enriched })
  );
};

const clearActiveCall = () => emitActiveCall(null);

const emitIncomingCall = (call?: RcCall | null) => {
  const parsed = parseIncomingCall(call);
  if (!parsed?.phoneNumber && !parsed?.name) return;
  clearInboundCallContext();
  clearCompletedCallLogSyncSessions();
  activeIncomingCall = parsed;
  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_INCOMING_EVENT, { detail: parsed })
  );
};

const clearIncomingCall = () => {
  if (!activeIncomingCall) return;
  activeIncomingCall = null;
  window.dispatchEvent(new CustomEvent(RING_CENTRAL_INCOMING_CLEAR_EVENT));
};

const resolvePartyDisplayName = (
  party?: RcCallParty,
  fallbackName?: string,
  phone?: string
) => party?.name || fallbackName || party?.extensionName || phone || null;

const parseCallEndDetail = (data?: WidgetMessage | null) => {
  const call = data?.call;
  if (!call) return null;
  const from = call.from || {};
  const to = call.to || {};
  const fromPhone =
    from.phoneNumber || from.extensionNumber || call.callerId || '';
  const toPhone = to.phoneNumber || to.extensionNumber || '';
  const phoneNumber = String(fromPhone || toPhone || '').trim();
  const recordingUri =
    call.recording?.contentUri || call.recording?.link || call.recording?.uri;

  const telephonySessionId = resolveTelephonySessionId(call);
  const sessionId = resolveLegacySessionId(call);

  return {
    callId: telephonySessionId || sessionId || call.id,
    telephonySessionId,
    sessionId,
    rcCallLogId: call.id,
    phoneNumber,
    from: String(fromPhone || ''),
    to: String(toPhone || ''),
    direction: call.direction,
    startTime: call.startTime,
    duration: call.duration,
    durationMs: call.durationMs,
    status: call.result || call.telephonyStatus,
    callerName:
      resolvePartyDisplayName(from, call.fromName, String(fromPhone || '')) ||
      undefined,
    receiverName:
      resolvePartyDisplayName(to, call.toName, String(toPhone || '')) ||
      undefined,
    recordingUri: recordingUri || undefined,
    transcript: call.transcript ?? null,
  };
};

const buildCallEndDetail = (
  data?: WidgetMessage | null,
  endingCall?: ActiveCallDetail | null,
  incomingCall?: IncomingCallDetail | null
): CallEndDetail | null => {
  const parsed = parseCallEndDetail(data);
  if (parsed) {
    const telephonySessionId =
      parsed.telephonySessionId ||
      incomingCall?.telephonySessionId ||
      endingCall?.telephonySessionId;
    const sessionId =
      parsed.sessionId || incomingCall?.sessionId || endingCall?.sessionId;
    const callId =
      telephonySessionId ||
      sessionId ||
      incomingCall?.callId ||
      endingCall?.callId ||
      parsed.callId;

    return {
      ...parsed,
      callId,
      telephonySessionId,
      sessionId,
      phoneNumber:
        parsed.phoneNumber ||
        incomingCall?.phoneNumber ||
        endingCall?.phoneNumber ||
        resolvePendingPhoneNumber() ||
        resolveLastDialedPhone() ||
        '',
    };
  }
  if (!endingCall?.callId && !endingCall?.phoneNumber) return null;

  const direction = String(endingCall.direction || '').toLowerCase();
  return {
    callId: endingCall.telephonySessionId || endingCall.callId,
    telephonySessionId: endingCall.telephonySessionId,
    sessionId: endingCall.sessionId,
    phoneNumber: endingCall.phoneNumber,
    from: direction.includes('outbound') ? '' : endingCall.phoneNumber,
    to: direction.includes('outbound') ? endingCall.phoneNumber : '',
    direction: endingCall.direction,
  };
};

const resolveLastDialedPhone = () => {
  if (!lastDialedPhone) return '';
  if (Date.now() - lastDialedAt > LAST_DIALED_PHONE_TTL_MS) {
    lastDialedPhone = null;
    return '';
  }
  return lastDialedPhone;
};

const rememberDialedPhone = (phoneNumber: string) => {
  if (!phoneNumber) return;
  lastDialedPhone = phoneNumber;
  lastDialedAt = Date.now();
};

const emitCallEnd = (
  data?: WidgetMessage | null,
  endingCall?: ActiveCallDetail | null,
  incomingCall?: IncomingCallDetail | null
) => {
  const detail = buildCallEndDetail(data, endingCall, incomingCall);
  if (!detail) return;
  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_CALL_END_EVENT, { detail })
  );
};

export const getRingCentralIntegrationSnapshot = () => ({
  loggedIn: isUserLoggedIn,
  dialerReady: isDialerReady,
  webphoneConnected: isWebphoneConnected,
  fromNumbers: [...availableFromNumbers],
  incomingCall: activeIncomingCall,
  activeCall,
});

const clearTimer = (timerRef: TimerRef) => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

const readinessTimerRef: TimerRef = { current: null };
const callStartTimerRef: TimerRef = { current: null };
const evaluateIntervalRef: IntervalRef = { current: null };

const clearEvaluatePolling = () => {
  if (evaluateIntervalRef.current) {
    clearInterval(evaluateIntervalRef.current);
    evaluateIntervalRef.current = null;
  }
};

const startEvaluatePolling = () => {
  clearEvaluatePolling();
  evaluateIntervalRef.current = setInterval(() => {
    if (!pendingDialNumber) {
      clearEvaluatePolling();
      return;
    }
    evaluateClickToCall();
  }, 1000);
};

export const normalizeToE164 = (rawNumber: string | number) => {
  let digits = formatPhoneNumber(String(rawNumber ?? ''));
  if (!digits) return '';

  if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits.startsWith('+') ? digits : `+${digits}`;
};

export const getDialPhoneCandidates = (rawNumber: string | number) => {
  const e164 = normalizeToE164(rawNumber);
  if (!e164) return [];

  const digits = e164.replace(/\D/g, '');
  const candidates: string[] = [];

  if (digits.length === 11 && digits.startsWith('1')) {
    candidates.push(digits.slice(1));
    candidates.push(digits);
    candidates.push(e164);
  } else if (digits.length === 10) {
    candidates.push(digits);
    candidates.push(`1${digits}`);
    candidates.push(e164);
  } else {
    candidates.push(e164);
  }

  return Array.from(new Set(candidates));
};

const getSoftphoneFrame = () =>
  document.getElementById(RING_CENTRAL_FRAME_ID) as HTMLIFrameElement | null;

const syncRcRecordsToCrm = async (records: SyncRecord[]) => {
  if (!records.length) return;

  const { syncRcCallRecords, isCallRecordSynced } = await import(
    '../components/CallHistory/services/call-history.service'
  );
  const { shouldSyncCallLogRecord } = await import('./ringCentralCallHistory');

  const toSync = records.filter(
    record => shouldSyncCallLogRecord(record) && !isCallRecordSynced(record)
  );

  if (!toSync.length) return;

  await syncRcCallRecords(toSync);
};

const processCallEndSync = async (
  _call?: RcCall | null,
  _endingCall?: ActiveCallDetail | null
) => {
  // Call-end payload is interim; sync runs on rc-call-history-synced-notify.
};

const cacheTelephonySession = (data: WidgetMessage) => {
  const session = data.telephonySession;
  if (!session?.id && !session?.sessionId) return;

  lastTelephonySession = {
    telephonySessionId: session.id,
    sessionId: session.sessionId,
    partyId: lastTelephonySession?.partyId,
  };
};

const resolveHistorySyncToken = (data?: WidgetMessage | null) => {
  const raw = data as Record<string, unknown> | undefined;
  const direct =
    data?.callLogSync?.syncInfo?.syncToken ||
    data?.syncInfo?.syncToken ||
    (typeof raw?.syncToken === 'string' ? raw.syncToken : undefined);
  if (direct) return direct;
  const body = data?.body as Record<string, unknown> | undefined;
  const payload = raw?.payload as Record<string, unknown> | undefined;
  const nested = [
    body?.syncInfo,
    body?.callLogSync,
    payload?.syncInfo,
    payload?.callLogSync,
    raw?.callLogSync,
  ];

  for (const item of nested) {
    if (item && typeof item === 'object') {
      const token = (
        item as { syncToken?: string; syncInfo?: { syncToken?: string } }
      ).syncToken;
      if (token) return token;
      const nestedToken = (item as { syncInfo?: { syncToken?: string } })
        .syncInfo?.syncToken;
      if (nestedToken) return nestedToken;
    }
  }

  return undefined;
};

const resolveInlineSyncRecords = (
  data?: WidgetMessage | null
): SyncRecord[] => {
  if (!data) return [];

  const raw = data as Record<string, unknown>;
  const body = data.body as
    | { callLogSync?: { records?: SyncRecord[] }; records?: SyncRecord[] }
    | undefined;
  const payload = raw.payload as
    | { callLogSync?: { records?: SyncRecord[] }; records?: SyncRecord[] }
    | undefined;
  const nestedCallLogSync = raw.callLogSync as
    | { records?: SyncRecord[] }
    | undefined;

  const candidates = [
    data.callLogSync?.records,
    data.records,
    body?.callLogSync?.records,
    body?.records,
    payload?.callLogSync?.records,
    payload?.records,
    nestedCallLogSync?.records,
  ];

  for (const item of candidates) {
    if (Array.isArray(item) && item.length) {
      return item;
    }
  }

  return [];
};

const HISTORY_SYNC_DELAY_MS = [1500, 3000];
let historySyncInFlight: Promise<void> | null = null;
const completedCallLogSyncSessions = new Set<string>();
let apiFetchAttemptedForSession: string | null = null;
let latestHistorySyncNotify: WidgetMessage | null = null;

const resolveCallLogSyncSessionKey = () => {
  const session = lastTelephonySession;
  if (!session) return null;
  return session.telephonySessionId || session.sessionId || null;
};

const clearCompletedCallLogSyncSessions = () => {
  completedCallLogSyncSessions.clear();
  apiFetchAttemptedForSession = null;
  latestHistorySyncNotify = null;
};

const processCallHistorySyncedNotify = async (data: WidgetMessage) => {
  latestHistorySyncNotify = data;

  const sessionKey = resolveCallLogSyncSessionKey();
  if (sessionKey && completedCallLogSyncSessions.has(sessionKey)) {
    return;
  }

  if (historySyncInFlight) {
    await historySyncInFlight;
  }

  historySyncInFlight = (async () => {
    const {
      fetchCallLogRecordsAfterHistorySync,
      filterRecentSyncRecords,
      shouldSyncCallLogRecord,
      persistCallLogSyncToken,
      matchesCallLogLookup,
    } = await import('./ringCentralCallHistory');
    const { isCallRecordSynced } = await import(
      '../components/CallHistory/services/call-history.service'
    );

    const persistLatestSyncToken = () => {
      const token = resolveHistorySyncToken(latestHistorySyncNotify ?? data);
      if (token) persistCallLogSyncToken(token);
    };
    persistLatestSyncToken();

    const lookup = lastTelephonySession
      ? {
          telephonySessionId: lastTelephonySession.telephonySessionId,
          sessionId: lastTelephonySession.sessionId,
          partyId: lastTelephonySession.partyId,
        }
      : null;

    const applyLookup = (records: SyncRecord[]) => {
      if (!lookup || !records.length) return records;
      const matched = records.filter(record =>
        matchesCallLogLookup(record, lookup)
      );
      return matched.length ? matched : records;
    };

    const resolveCurrentInlineRecords = () =>
      resolveInlineSyncRecords(latestHistorySyncNotify ?? data);

    const tryProcessRecords = async (records: SyncRecord[]) => {
      const readyRecords = filterRecentSyncRecords(applyLookup(records)).filter(
        shouldSyncCallLogRecord
      );
      if (!readyRecords.length) {
        return false;
      }

      const pendingRecords = readyRecords.filter(
        record => !isCallRecordSynced(record)
      );
      if (!pendingRecords.length) {
        if (sessionKey) completedCallLogSyncSessions.add(sessionKey);
        return true;
      }

      await syncRcRecordsToCrm(readyRecords);
      if (sessionKey) completedCallLogSyncSessions.add(sessionKey);
      return true;
    };

    const inlineFromNotify = resolveCurrentInlineRecords();
    if (
      inlineFromNotify.length &&
      (await tryProcessRecords(inlineFromNotify))
    ) {
      return;
    }

    for (const delayMs of HISTORY_SYNC_DELAY_MS) {
      if (delayMs) {
        await new Promise<void>(resolve => {
          window.setTimeout(resolve, delayMs);
        });
      }

      if (sessionKey && completedCallLogSyncSessions.has(sessionKey)) {
        return;
      }

      persistLatestSyncToken();

      const inlineRecords = resolveCurrentInlineRecords();
      if (inlineRecords.length) {
        if (await tryProcessRecords(inlineRecords)) {
          return;
        }
        continue;
      }

      if (sessionKey && apiFetchAttemptedForSession === sessionKey) {
        continue;
      }

      const records = await fetchCallLogRecordsAfterHistorySync(lookup);
      if (sessionKey) {
        apiFetchAttemptedForSession = sessionKey;
      }

      if (await tryProcessRecords(records)) {
        return;
      }
    }
  })();

  try {
    await historySyncInFlight;
  } finally {
    historySyncInFlight = null;
  }
};

const resolvePendingPhoneNumber = () => {
  const candidates = getDialPhoneCandidates(
    pendingDialRaw || pendingDialNumber || ''
  );
  return (
    candidates.find(c => /^\d{10}$/.test(c)) ||
    candidates[0] ||
    pendingDialNumber
  );
};

const resolveOutboundFromNumber = () => availableFromNumbers[0] || null;

const hangupSoftphoneCalls = () => {
  const frame = getSoftphoneFrame();
  if (!frame?.contentWindow) return;

  frame.contentWindow.postMessage(
    {
      type: 'rc-adapter-control-call',
      callAction: 'hangup',
    },
    '*'
  );
};

export const answerSoftphoneCall = () => {
  const frame = getSoftphoneFrame();
  if (!frame?.contentWindow) return false;

  frame.contentWindow.postMessage(
    {
      type: 'rc-adapter-control-call',
      callAction: 'answer',
    },
    '*'
  );
  return true;
};

export const rejectSoftphoneCall = () => {
  const frame = getSoftphoneFrame();
  if (!frame?.contentWindow) return false;

  frame.contentWindow.postMessage(
    {
      type: 'rc-adapter-control-call',
      callAction: 'reject',
    },
    '*'
  );
  hangupSoftphoneCalls();
  return true;
};

const clearPendingDial = () => {
  pendingDialNumber = null;
  pendingDialRaw = null;
  outboundCallPlaced = false;
  clearTimer(readinessTimerRef);
  clearTimer(callStartTimerRef);
  clearEvaluatePolling();
};

const notifyDialFailed = (detail?: string) => {
  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_DIAL_FAILED_EVENT, {
      detail: {
        summary: 'Call failed',
        detail:
          detail ||
          'Could not make the call. Open Phone, sign in, pick a caller ID in Settings, then try again.',
      },
    })
  );
};

const finishClickToCallAsFailed = (message?: string) => {
  hangupSoftphoneCalls();
  clearPendingDial();
  notifyDialFailed(message);
};

const finishClickToCallAsSuccess = () => {
  clearTimer(callStartTimerRef);
  clearTimer(readinessTimerRef);
  clearPendingDial();
};

const postToSoftphone = (payload: Record<string, unknown>) => {
  const frame = getSoftphoneFrame();
  if (!frame?.contentWindow) return false;
  frame.contentWindow.postMessage(payload, '*');
  return true;
};

export const triggerCallLoggerMatch = (
  sessionIds: Array<string | null | undefined>
) => {
  const ids = sessionIds.filter((id): id is string => Boolean(id));
  if (!ids.length) return;
  postToSoftphone({
    type: 'rc-adapter-trigger-call-logger-match',
    sessionIds: ids,
  });
};

const extractAccessTokenFromUnknown = (
  value: unknown,
  depth = 0
): string | null => {
  if (value == null || depth > 10) return null;

  if (typeof value === 'string') {
    const uriMatch = value.match(/[?&]access_token=([^&]+)/);
    if (uriMatch?.[1]) return decodeURIComponent(uriMatch[1]);
    return null;
  }

  if (typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  for (const key of ['access_token', 'accessToken']) {
    const token = record[key];
    if (typeof token === 'string' && token.length > 20) return token;
  }

  for (const nested of Object.values(record)) {
    const token = extractAccessTokenFromUnknown(nested, depth + 1);
    if (token) return token;
  }

  return null;
};

const mirrorTokenFromWidgetMessage = (data: unknown) => {
  const token = extractAccessTokenFromUnknown(data);
  if (!token) return;

  void import('./ringCentralCallHistory').then(
    ({ mirrorRingCentralAccessToken }) => {
      mirrorRingCentralAccessToken(token);
    }
  );
};

const syncRingCentralAccessToken = () => {
  if (!isUserLoggedIn) return;
  void import('./ringCentralCallHistory').then(
    ({ syncRingCentralAccessTokenFromWidget }) => {
      void syncRingCentralAccessTokenFromWidget();
    }
  );
};

const disableRingCentralAutoLog = () => {
  postToSoftphone({
    type: 'rc-adapter-set-call-logger-auto-log',
    autoLog: false,
  });
};

const registerRingCentralCrmService = () => {
  if (!isUserLoggedIn || rcCrmServiceRegistered) {
    return;
  }
  const service = {
    name: RC_CRM_SERVICE_NAME,
    callLoggerPath: RC_CRM_CALL_LOGGER_PATH,
    callLoggerTitle: 'CRM',
    callLogEntityMatcherPath: RC_CRM_CALL_LOGGER_MATCH_PATH,
    recordingWithToken: 1,
    callLoggerAutoLogSettingHidden: true,
    callLoggerAutoLogOnCallSync: 0,
  };
  const registered = postToSoftphone({
    type: 'rc-adapter-register-third-party-service',
    service,
  });
  if (registered) {
    rcCrmServiceRegistered = true;
    disableRingCentralAutoLog();
  }
};

const respondToWidgetPostMessage = (
  requestId: string,
  response: Record<string, unknown>
) => {
  postToSoftphone({
    type: 'rc-post-message-response',
    responseId: requestId,
    response,
  });
};

const extractMatcherSessionIds = (body?: Record<string, unknown>) => {
  if (!body) return [] as string[];

  if (Array.isArray(body.sessionIds)) {
    return body.sessionIds.map(String).filter(Boolean);
  }

  const keyed = Object.keys(body).filter(
    key => /^\d{9,}$/.test(key) || key.startsWith('s-')
  );
  if (keyed.length) return keyed;

  return [];
};

const handleCrmCallLoggerRequest = (data: WidgetMessage): boolean => {
  if (data.type !== 'rc-post-message-request' || !data.requestId) {
    return false;
  }

  if (data.path === RC_CRM_CALL_LOGGER_PATH) {
    respondToWidgetPostMessage(data.requestId, { data: 'ok' });
    return true;
  }

  if (data.path === RC_CRM_CALL_LOGGER_MATCH_PATH) {
    const sessionIds = extractMatcherSessionIds(
      data.body as Record<string, unknown> | undefined
    );
    void import('../components/CallHistory/services/call-history.service').then(
      ({ getCallLogMatcherData }) => {
        respondToWidgetPostMessage(data.requestId!, {
          data: getCallLogMatcherData(sessionIds),
        });
      }
    );
    return true;
  }

  return false;
};

export const postDialToSoftphone = (
  phoneNumber: string,
  { toCall = true, fromNumber }: { toCall?: boolean; fromNumber?: string } = {}
) => {
  if (!phoneNumber) return false;

  const msg: Record<string, unknown> = {
    type: 'rc-adapter-new-call',
    phoneNumber,
    toCall,
  };
  if (fromNumber) {
    msg.fromNumber = fromNumber;
  }
  return postToSoftphone(msg);
};

const applyCallingSettings = () => {
  const fromNumber = resolveOutboundFromNumber();
  const payload: Record<string, unknown> = {
    type: 'rc-calling-settings-update',
    callWith: 'browser',
  };
  if (fromNumber) {
    payload.fromNumber = fromNumber;
  }
  postToSoftphone(payload);
};

export const navigateSoftphoneToDialer = () =>
  postToSoftphone({
    type: 'rc-adapter-navigate-to',
    path: '/dialer',
  });

const softphoneNavigationTimers: ReturnType<typeof setTimeout>[] = [];
let pendingSoftphonePath: string | null = null;

const clearSoftphoneNavigationTimers = () => {
  softphoneNavigationTimers.forEach(id => clearTimeout(id));
  softphoneNavigationTimers.length = 0;
};

const navigateSoftphoneToPath = (path: string) =>
  postToSoftphone({
    type: 'rc-adapter-navigate-to',
    path,
  });

export const scheduleSoftphoneNavigation = (path = '/dialer') => {
  pendingSoftphonePath = path;
  clearSoftphoneNavigationTimers();

  const attemptNavigate = () => {
    if (pendingSoftphonePath !== path) return;
    navigateSoftphoneToPath(path);
    if (isDialerReady) {
      cancelSoftphoneNavigation();
    }
  };

  attemptNavigate();
  if (isDialerReady) return;

  const delays = [400, 800, 1500, 2500];
  delays.forEach(delay => {
    const id = setTimeout(attemptNavigate, delay);
    softphoneNavigationTimers.push(id);
  });
};

export const ensureSoftphoneOnDialer = () => {
  scheduleSoftphoneNavigation('/dialer');
};

export const cancelSoftphoneNavigation = () => {
  pendingSoftphonePath = null;
  clearSoftphoneNavigationTimers();
};

const prefillPendingInDialer = () => {
  if (!pendingDialNumber || !getSoftphoneFrame()?.contentWindow) {
    return false;
  }
  navigateSoftphoneToDialer();
  postDialToSoftphone(resolvePendingPhoneNumber() || '', { toCall: false });
  return true;
};

const canPlaceOutboundCall = () =>
  Boolean(
    isUserLoggedIn &&
      isDialerReady &&
      isWebphoneConnected &&
      getSoftphoneFrame()?.contentWindow &&
      resolveOutboundFromNumber()
  );

const placeOutboundCallOnce = () => {
  if (outboundCallPlaced || !pendingDialNumber) return;

  if (!canPlaceOutboundCall()) {
    return;
  }

  outboundCallPlaced = true;
  clearTimer(readinessTimerRef);

  const phoneNumber = resolvePendingPhoneNumber();
  const fromNumber = resolveOutboundFromNumber();

  applyCallingSettings();
  navigateSoftphoneToDialer();

  setTimeout(() => {
    const posted = postDialToSoftphone(phoneNumber || '', {
      toCall: true,
      fromNumber: fromNumber || undefined,
    });
    if (!posted) {
      outboundCallPlaced = false;
    }

    clearTimer(callStartTimerRef);
    callStartTimerRef.current = setTimeout(() => {
      if (pendingDialNumber) {
        finishClickToCallAsFailed(
          'Could not make the call. Check caller ID in Phone → Settings → Calling.'
        );
      }
    }, CALL_START_WAIT_MS);
  }, 400);
};

const evaluateClickToCall = () => {
  if (!pendingDialNumber || outboundCallPlaced) return;

  prefillPendingInDialer();

  if (canPlaceOutboundCall()) {
    placeOutboundCallOnce();
  }
};

const startReadinessWait = () => {
  clearTimer(readinessTimerRef);
  readinessTimerRef.current = setTimeout(() => {
    if (!pendingDialNumber || outboundCallPlaced) return;

    if (!isUserLoggedIn) {
      finishClickToCallAsFailed(
        'Could not make the call. Sign in to Phone first.'
      );
      return;
    }

    if (!resolveOutboundFromNumber()) {
      finishClickToCallAsFailed(
        'Could not make the call. Set a caller ID in Phone → Settings → Calling.'
      );
      return;
    }

    if (!isWebphoneConnected) {
      finishClickToCallAsFailed(
        'Could not make the call. Wait for the phone to finish connecting, or allow microphone access.'
      );
      return;
    }

    finishClickToCallAsFailed();
  }, READINESS_WAIT_MS);
};

const RC_OVERRIDES_CSS_ID = 'pf-rc-softphone-overrides';
const RC_OVERRIDES_INLINE_STYLE_ID = 'pf-rc-softphone-hide-ui';

let callsScrollFixObserver: MutationObserver | null = null;

const injectSoftphoneOverrides = () => {
  const frame = getSoftphoneFrame();
  const doc = frame?.contentDocument;
  if (!doc) return;

  const href = buildRingCentralStylesUri();
  let link = doc.getElementById(RC_OVERRIDES_CSS_ID) as HTMLLinkElement | null;
  if (!link) {
    link = doc.createElement('link');
    link.id = RC_OVERRIDES_CSS_ID;
    link.rel = 'stylesheet';
    doc.head.appendChild(link);
  }
  if (link.href !== href) {
    link.href = href;
  }

  let inlineStyle = doc.getElementById(
    RC_OVERRIDES_INLINE_STYLE_ID
  ) as HTMLStyleElement | null;
  if (!inlineStyle) {
    inlineStyle = doc.createElement('style');
    inlineStyle.id = RC_OVERRIDES_INLINE_STYLE_ID;
    doc.head.appendChild(inlineStyle);
  }
  inlineStyle.textContent = RING_CENTRAL_UI_HIDE_CSS;
};

const hideCrmLogButtons = (doc: Document) => {
  doc
    .querySelectorAll(
      'button[data-sign="log"], [class*="call-item-action-menu"] > button:first-child, button[aria-label="CRM"][title="CRM"]'
    )
    .forEach(node => {
      const el = node as HTMLElement;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
    });
};

const fixCallsTabScroll = () => {
  const frame = getSoftphoneFrame();
  const doc = frame?.contentDocument;
  if (!doc) return;

  injectSoftphoneOverrides();
  hideCrmLogButtons(doc);

  const hasCallsList = doc.querySelector('[class*="CallsListPanel"]');
  if (!hasCallsList) return;

  doc.querySelectorAll('main[role="main"], main').forEach(node => {
    const main = node as HTMLElement;
    main.style.setProperty('overflow-x', 'hidden', 'important');
    main.style.setProperty('overflow-y', 'auto', 'important');
  });

  doc
    .querySelectorAll(
      '[class*="CallsListPanel_root"], [class*="CallsListPanel_list"]'
    )
    .forEach(node => {
      const el = node as HTMLElement;
      el.style.setProperty('overflow', 'hidden', 'important');
      el.style.setProperty('display', 'flex', 'important');
      el.style.setProperty('flex-direction', 'column', 'important');
      el.style.setProperty('min-height', '0', 'important');
    });

  doc
    .querySelectorAll(
      '[data-virtuoso-scroller], [data-test-id="virtuoso-scroller"]'
    )
    .forEach(node => {
      const scroller = node as HTMLElement;
      scroller.style.setProperty('height', 'auto', 'important');
      scroller.style.setProperty('max-height', 'none', 'important');
      scroller.style.setProperty('overflow', 'visible', 'important');

      let parent = scroller.parentElement;
      while (parent && parent !== doc.documentElement) {
        if (parent.tagName !== 'MAIN') {
          (parent as HTMLElement).style.setProperty(
            'overflow',
            'hidden',
            'important'
          );
        }
        parent = parent.parentElement;
      }
    });
};

const scheduleCallsScrollFix = () => {
  injectSoftphoneOverrides();
  fixCallsTabScroll();
  [100, 400, 1000, 2500].forEach(ms => {
    window.setTimeout(fixCallsTabScroll, ms);
  });
};

const startCallsScrollFixObserver = () => {
  const frame = getSoftphoneFrame();
  const doc = frame?.contentDocument;
  if (!doc) return;

  scheduleCallsScrollFix();

  if (callsScrollFixObserver) return;

  let rafId = 0;
  callsScrollFixObserver = new MutationObserver(() => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      fixCallsTabScroll();
    });
  });

  callsScrollFixObserver.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });
};

export const onRingCentralFrameLoaded = () => {
  registerRingCentralCrmService();
  syncRingCentralAccessToken();
  emitStatus();
  evaluateClickToCall();
  startCallsScrollFixObserver();
};

const isRingCentralWidgetMessage = (event: MessageEvent) => {
  const frame = getSoftphoneFrame();
  if (frame?.contentWindow && event.source === frame.contentWindow) {
    return true;
  }
  try {
    return (event.origin || '').includes('ringcentral.com');
  } catch {
    return false;
  }
};

const handleWidgetMessage = (event: MessageEvent) => {
  if (!isRingCentralWidgetMessage(event)) {
    return;
  }

  const data = event?.data as WidgetMessage | undefined;
  if (!data?.type) return;

  mirrorTokenFromWidgetMessage(data);

  if (handleCrmCallLoggerRequest(data)) {
    return;
  }

  switch (data.type) {
    case 'rc-dialer-status-notify':
      isDialerReady = Boolean(data.ready);
      if (isDialerReady) {
        registerRingCentralCrmService();
      }
      if (isDialerReady && pendingSoftphonePath) {
        navigateSoftphoneToPath(pendingSoftphonePath);
        cancelSoftphoneNavigation();
      }
      emitStatus();
      evaluateClickToCall();
      break;
    case 'rc-login-status-notify':
      isUserLoggedIn = Boolean(data.loggedIn);
      if (!isUserLoggedIn) {
        rcCrmServiceRegistered = false;
        isDialerReady = false;
        isWebphoneConnected = false;
        availableFromNumbers = [];
        clearIncomingCall();
        clearActiveCall();
        cancelSoftphoneNavigation();
        clearPfRcAccessToken();
      } else {
        registerRingCentralCrmService();
        syncRingCentralAccessToken();
        void import('./ringCentralCallHistory').then(
          ({ refreshPriorCallLogSyncTokenFromStorage }) => {
            refreshPriorCallLogSyncTokenFromStorage();
          }
        );
        if (pendingSoftphonePath) {
          navigateSoftphoneToPath(pendingSoftphonePath);
        }
      }
      emitStatus();
      evaluateClickToCall();
      break;
    case 'rc-webphone-connection-status-notify':
      isWebphoneConnected =
        data.connectionStatus === 'connectionStatus-connected';
      emitStatus();
      evaluateClickToCall();
      break;
    case 'rc-calling-settings-notify': {
      const fromNumbers = data.callSetting?.fromNumbers || data.fromNumbers;
      if (Array.isArray(fromNumbers) && fromNumbers.length) {
        availableFromNumbers = fromNumbers;
        applyCallingSettings();
        emitStatus();
        evaluateClickToCall();
      }
      break;
    }
    case 'rc-call-ring-notify':
      if (isInboundRinging(data.call)) {
        emitIncomingCall(data.call);
      }
      break;
    case 'rc-active-call-notify':
      if (isInboundRinging(data.call)) {
        emitIncomingCall(data.call);
        clearActiveCall();
      } else if (isConnectedCall(data.call)) {
        clearIncomingCall();
        const parsed = parseActiveCall(data.call);
        if (parsed) {
          emitActiveCall(parsed);
        }
        cancelSoftphoneNavigation();
      } else {
        const incoming = activeIncomingCall;
        clearIncomingCall();
        clearActiveCall();
        if (
          incoming &&
          String(incoming.direction || '')
            .toLowerCase()
            .includes('inbound')
        ) {
          // dispatchInboundMissedCallSync(incoming);
        }
      }
      break;
    case 'rc-call-init-notify': {
      const wasClickToCall = clickToCallActive;
      finishClickToCallAsSuccess();
      clearCompletedCallLogSyncSessions();
      if (!wasClickToCall) {
        removeLocalStorageValue(RC_CUSTOMER_ID_KEY);
      }
      clearIncomingCall();
      if (data.call) {
        const parsed = parseActiveCall(data.call);
        if (parsed) {
          emitActiveCall(parsed, { connectedNow: true });
        }
        cancelSoftphoneNavigation();
      }
      break;
    }
    case 'rc-call-start-notify':
      finishClickToCallAsSuccess();
      clearCompletedCallLogSyncSessions();
      clearIncomingCall();
      if (data.call) {
        const parsed = parseActiveCall(data.call);
        if (parsed) {
          emitActiveCall(parsed, { connectedNow: true });
        }
        cancelSoftphoneNavigation();
      }
      break;
    case 'rc-call-end-notify': {
      const incoming = activeIncomingCall;
      clearIncomingCall();
      const endingCall = activeCall;
      clearActiveCall();
      emitCallEnd(data, endingCall, incoming);
      if (data.call?.id) {
        lastTelephonySession = {
          ...lastTelephonySession,
          partyId: data.call.id,
        };
      }
      void processCallEndSync(data.call, endingCall);
      break;
    }
    case 'rc-telephony-session-notify':
      cacheTelephonySession(data);
      break;
    case 'rc-callLogger-auto-log-notify':
      if (data.autoLog) {
        disableRingCentralAutoLog();
      }
      break;
    case 'rc-call-history-synced-notify': {
      void processCallHistorySyncedNotify(data);
      break;
    }
    default:
      break;
  }
};

export const initRingCentralMessageListener = () => {
  if (dialListenerInitialized || typeof window === 'undefined') return;
  dialListenerInitialized = true;
  void import('../components/CallHistory/services/call-history.service');
  window.addEventListener('message', handleWidgetMessage);
};

export const dialWithRingCentral = (
  rawNumber: string | number,
  options?: DialWithRingCentralOptions
) => {
  if (!isRingCentralEnabled()) return false;
  if (rawNumber == null || rawNumber === '') return false;

  clickToCallActive = true;
  storeOutboundCallContext({
    customerId: options?.customerId,
    projectId: options?.projectId,
  });

  const raw = String(rawNumber);
  const candidates = getDialPhoneCandidates(raw);
  const phoneNumber = candidates[0];
  if (!phoneNumber) {
    clearOutboundCallContext();
    return false;
  }

  clearPendingDial();
  outboundCallPlaced = false;

  pendingDialRaw = raw;
  pendingDialNumber = phoneNumber;
  rememberDialedPhone(phoneNumber);

  window.dispatchEvent(
    new CustomEvent(RING_CENTRAL_DIAL_EVENT, { detail: { phoneNumber } })
  );
  window.dispatchEvent(new CustomEvent(RING_CENTRAL_OPEN_EVENT));

  startReadinessWait();
  startEvaluatePolling();
  setTimeout(() => evaluateClickToCall(), 500);

  return true;
};

export const RING_CENTRAL_EVENTS = {
  OPEN: RING_CENTRAL_OPEN_EVENT,
  DIAL: RING_CENTRAL_DIAL_EVENT,
  DIAL_FAILED: RING_CENTRAL_DIAL_FAILED_EVENT,
  STATUS: RING_CENTRAL_STATUS_EVENT,
  INCOMING: RING_CENTRAL_INCOMING_EVENT,
  INCOMING_CLEAR: RING_CENTRAL_INCOMING_CLEAR_EVENT,
  CALL_END: RING_CENTRAL_CALL_END_EVENT,
  CALL_HISTORY_SYNCED: RING_CENTRAL_CALL_HISTORY_SYNCED_EVENT,
  ACTIVE_CALL: RING_CENTRAL_ACTIVE_CALL_EVENT,
  INBOUND_PROJECT_LINKED: RING_CENTRAL_INBOUND_PROJECT_LINKED_EVENT,
};
